import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Button } from 'reactstrap';
import { toggleSMLPricingModal } from '../../../redux/slices/appInfo/slice';
import { TypedMemo } from '../../common/types';
import { useWorkspaceOwner } from '../../team/useWorkspaceOwner';
import { emailInitials } from '../../team/emailInitials';
import { teamOwnerMessages } from '../../team/teamOwnerMessages';
import { Panel, PanelBody } from '../../music24/components/primitives/Panel';
import styles from './SubscriptionMemberNotice.module.scss';

/**
 * Design o6: what a team member sees instead of the owner's billing view (ruling 8). Adds the
 * owner's row — their initials and email — under the existing notice, so the member knows whom
 * to ask. "See what the plan includes" opens the plans window, which a member sees read-only (o5).
 */
const SubscriptionMemberNoticeComponent: React.FC = () => {
  const intl = useIntl();
  const owner = useWorkspaceOwner();
  const dispatch = useDispatch();
  const openPlans = useCallback(() => {
    dispatch(toggleSMLPricingModal({ shouldShow: true }));
  }, [dispatch]);
  return (
    <Panel as="section">
      <PanelBody stack>
        <div>
          {/* The page head (SettingsPageHead) shows the title "Subscription". */}
          <p className="text-muted mb-0">
            {intl.formatMessage({
              id: 'app.sml.settings.subscription.memberNotice',
              defaultMessage: 'The workspace owner manages this subscription. Your plan comes from their workspace.',
            })}
          </p>
        </div>
        {owner !== null && (
          <div className={styles.owner}>
            <div className={styles.avatar} aria-hidden="true">
              {emailInitials(owner.email)}
            </div>
            <div>
              <div className="fw-bold" data-clarity-mask="true">
                {owner.email}
              </div>
              <small className="text-muted">{intl.formatMessage(teamOwnerMessages.caption)}</small>
            </div>
          </div>
        )}
        <div>
          <Button color="link" className="p-0" onClick={openPlans}>
            {intl.formatMessage({
              id: 'app.sml.settings.subscription.memberSeePlan',
              defaultMessage: 'See what the plan includes',
            })}
          </Button>
        </div>
      </PanelBody>
    </Panel>
  );
};

export const SubscriptionMemberNotice = TypedMemo(SubscriptionMemberNoticeComponent);
